import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type DeclineRequestDialogProps = {
    open: boolean;
    /** The requester told that they were declined — shown in the title and the text. */
    partyName?: string | null;
    /** True while the decline mutation runs. */
    isDeclining: boolean;
    /** Cancel or close: the request stays pending. */
    onClose: () => void;
    /** Decline with the trimmed reason; undefined when none was given. */
    onConfirm: (reason: string | undefined) => void;
};

const MAX_REASON_LENGTH = 500;

/**
 * DD-3707: confirms declining a connection request (supplier or clinic request alike),
 * with an optional reason shown to the requester and carried in the decline notification.
 */
const DeclineRequestDialog: FC<DeclineRequestDialogProps> = ({ open, partyName, isDeclining, onClose, onConfirm }) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const [reason, setReason] = useState('');

    // The parent closes the dialog on success without calling onClose, so reset here.
    useEffect(() => {
        if (!open) setReason('');
    }, [open]);

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            title={translate('client-directory:DECLINE_REQUEST_TITLE', { name: partyName })}
            actions={
                <>
                    <Button onClick={onClose} disabled={isDeclining} data-test="decline-request-dialog.cancel">
                        {translate('common:CANCEL')}
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={isDeclining}
                        onClick={() => onConfirm(reason.trim() || undefined)}
                        data-test="decline-request-dialog.confirm"
                    >
                        {translate('client-directory:DECLINE_SUPPLIER_REQUEST')}
                    </LoadingButton>
                </>
            }
        >
            <Typography variant="body2">
                {translate('client-directory:DECLINE_SUPPLIER_REQUEST_TEXT', { lab: partyName })}
            </Typography>
            <TextField
                sx={{ mt: 2 }}
                fullWidth
                multiline
                minRows={4}
                label={translate('client-directory:DECLINE_REQUEST_REASON_LABEL')}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                inputProps={{ maxLength: MAX_REASON_LENGTH, 'data-test': 'decline-request-dialog.reason-input' }}
                helperText={`${reason.length}/${MAX_REASON_LENGTH}`}
            />
        </DHDialog>
    );
};

export default DeclineRequestDialog;
